/**
 * "3 Steps to Value": the new-creator setup card on the dashboard.
 *
 * A brand-new creator lands on a dashboard full of empty numbers and doesn't
 * know where to start. This card walks them through the three actions that
 * make Bearri work for them: connect a channel, find their first brands, send
 * their first pitch. Each finished step adds one extra report, kept for as
 * long as the server says (the date comes back with the grant). The last step
 * starts the reply loop.
 *
 * The server is the only authority here. It decides whether a step is done,
 * how many reports a step adds, and when a bonus stops counting; this card
 * only shows what it is told, and hides a number it wasn't sent rather than
 * guess one. The same call grants any bonus that was earned and hands back
 * the ones the creator hasn't been told about yet, marking them told as it
 * answers. So the answer is the only chance to announce them: the toast (one
 * per load, summing them all) and the usage refresh run whenever that answer
 * arrives, even if the card was unmounted meanwhile or every step is done and
 * the card itself is gone. Only this card's own state waits on it being
 * mounted. A run that is cancelled before it asks (React's StrictMode double
 * mount) never asks, so it can't swallow the other run's bonus. Each bonus is
 * announced at most once; it is only missed if the answer never reaches the
 * page at all. After a toast the usage summary is refreshed so every report
 * count on the page includes the bonus at once.
 *
 * The card fetches its own progress once per sign-in and disappears entirely
 * once all three steps are done: a setup checklist that lingers after you've
 * set up is clutter.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card } from '../ui/Card';
import { useOptionalSubscription } from '../../contexts/SubscriptionContext';
import {
  getOnboardingProgress,
  OnboardingGrant,
  OnboardingProgress,
  OnboardingStep,
} from '../../services/onboardingService';
import './dashboard.css';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** A report count the server actually sent: a whole number above zero. Anything else is never shown. */
const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) > 0;

/** "2027-09-29" → "Sep 29, 2027", read as a calendar date (never shifted a day by the viewer's time zone). */
function formatDay(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}

/** One line for every bonus in this response, or null when there's nothing new to say. */
function grantToast(grants: OnboardingGrant[] | undefined): string | null {
  const counted = (grants ?? []).filter((g) => isCount(g.reports));
  const added = counted.reduce((n, g) => n + g.reports, 0);
  if (added <= 0) return null;
  // The earliest date is the honest one: every bonus in this toast is kept at least that long.
  const earliest = counted
    .map((g) => g.expires_on)
    .filter((d): d is string => typeof d === 'string' && formatDay(d) !== null)
    .sort()[0];
  const until = earliest ? formatDay(earliest) : null;
  const words = `+${added} extra ${plural(added, 'report', 'reports')} added`;
  return until ? `${words} · kept until ${until}` : words;
}

const ThreeStepsCard: React.FC = () => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [failed, setFailed] = useState(false);

  // The usage refresh is read through a ref and deliberately kept OUT of the
  // effect's dependency list: the provider hands out a new function whenever
  // its own state changes, and an effect that re-ran on that would re-fetch,
  // re-toast and re-refresh in a loop (a self-retriggering effect once caused
  // a request storm).
  const sub = useOptionalSubscription();
  const refreshUsage = useRef(sub?.refreshUsageSummary);
  refreshUsage.current = sub?.refreshUsageSummary;

  useEffect(() => {
    // Wait for Clerk before asking for a token: calling getToken() on a cold
    // mount rejects before Clerk initialises. One attempt, no retry-on-error
    // state that could re-fire itself; the card simply stays hidden if this
    // fails.
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        // Cancelled before asking (StrictMode's throwaway first mount, or a
        // quick navigation): don't ask at all. Asking would mark the bonuses
        // told on an answer nobody reads.
        if (cancelled) return;
        if (!token) throw new Error('no token');
        const p = await getOnboardingProgress(token);
        // Once asked, the answer has already marked any new bonus told, so
        // announce it whether or not the card is still mounted: the toaster
        // and the usage summary live above this card.
        const line = grantToast(p.just_granted);
        if (line) {
          toast.success(line);
          Promise.resolve(refreshUsage.current?.()).catch(() => undefined);
        }
        if (!cancelled) setProgress(p);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, isLoaded, isSignedIn]);

  // Hidden until we know there's something to do, and gone once done. A
  // failed fetch also hides it: setup help is never worth an error.
  if (failed || !progress || progress.all_done) return null;

  const doneCount = progress.steps.filter((s) => s.done).length;
  // The first not-yet-done step is the one to emphasize.
  const nextKey = progress.steps.find((s) => !s.done)?.key;

  return (
    <Card padding="none" className="br-onboarding overflow-hidden">
      <div className="br-onboarding__header">
        <h2>Get set up. Earn extra reports.</h2>
        <p>{doneCount} of {progress.steps.length} steps complete</p>
      </div>

      <ol className="br-onboarding__steps">
        {progress.steps.map((step, i) => (
          <StepRow
            key={step.key}
            step={step}
            index={i}
            isNext={step.key === nextKey}
            onGo={() => navigate(step.href)}
          />
        ))}
      </ol>
    </Card>
  );
};

const StepRow: React.FC<{
  step: OnboardingStep;
  index: number;
  isNext: boolean;
  onGo: () => void;
}> = ({ step, index, isNext, onGo }) => {
  // The chip only shows a count the server sent; an older or partial answer
  // without one gets no chip rather than "+undefined".
  const reward = !isCount(step.reports)
    ? null
    : step.done
      ? `+${step.reports} added`
      : `+${step.reports} ${plural(step.reports, 'report', 'reports')}`;
  return (
    <li className={`br-onboarding__step${isNext ? ' br-onboarding__step--next' : ''}${step.done ? ' br-onboarding__step--done' : ''}`}>
      <div className="br-onboarding__step-heading">
        {/* Status marker: the step's number in a printed box; a done step is the box ticked with the pen (CSS). */}
        <span className={`br-step-dot${step.done ? ' br-step-dot--done' : isNext ? ' br-step-dot--next' : ''}`}>
          {step.done ? <span className="sr-only">Done</span> : index + 1}
        </span>

        <h3>{step.title}</h3>
      </div>
      {reward && <span className="br-onboarding__reward br-count br-count--green self-start">{reward}</span>}
      {isNext && <p>{step.blurb}</p>}

      {/* the next step is the one blue slab on the sheet; the others wait in cream */}
      {!step.done && (
        <button
          type="button"
          onClick={onGo}
          className={`br-slab br-slab--sm ${isNext ? 'br-slab--blue' : 'br-slab--cream'}`}
        >
          {step.cta}
        </button>
      )}
    </li>
  );
};

export default ThreeStepsCard;
